import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { Registration } from "../../../models/registration";
import { RegistrationControllerService } from "../../../services/registration-controller/registration-controller.service";
import { RegistrationService } from "../../../services/registration-controller/registration.service";
import { TranslatedBaseError } from "../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { Service } from "../../../models/service";
import { Context } from "../../../models/context";
import { ServiceDetailComponent } from "../service-detail/service-detail.component";
import { Store } from "../../../services/store.service";
import { ServiceControllerService } from "../../../services/service-controller/service-controller.service";
import { RecoverOrphanConfig } from "../../../models/recover-orphan-config";

@Component({
  selector: "app-registrations",
  templateUrl: "./registrations.component.html",
  providers: [RegistrationControllerService],
})
export class RegistrationsComponent extends TranslatedBaseError implements OnInit {

  @ViewChild("ServiceDetailComponent") public serviceDetailComponent: ServiceDetailComponent;

  public allRegistrations: Registration[];
  public filteredAllRegistrations: Registration[];
  public serviceRegistrations: Registration[];
  public serviceRegistrationActive: Registration[];
  public serviceId: string;
  public context: Context = new Context();
  public baseUrl: string;
  public newRegistration: Registration = new Registration();
  @Input() public baseUrlSuffix: string;
  @Input() public hideList: boolean = false;
  @Input() public determinantMapById: { [key: string]: string };
  public showRegistrationAdd: boolean = false;
  public subscription: any;
  public loadedServiceRegistrations: boolean = false;
  public loadedAllRegistrations: boolean = false;
  public newCustomFilterBy: string;

  public defaultOtherRegListCollapsed: boolean = false;
  public debugMode: boolean = false;
  public registrationErrors: { [key: string]: number } = {};

  constructor(private registrationController: RegistrationControllerService,
    private registrationService: RegistrationService,
    private serviceControllerService: ServiceControllerService,
    public store: Store,
    private router: Router,
    private route: ActivatedRoute,
    toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      if (this.baseUrlSuffix) {
        this.baseUrl = "/services/" + this.serviceId + this.baseUrlSuffix;
      } else {
        this.baseUrl = "/services/" + this.serviceId + "/registrations";
      }
      this.init();
    });
    this.subscription = this.registrationService.changed.subscribe((event) => {
      if (event.change) {
        this.getServiceRegistrations(this.serviceId);
      }
    });
    this.store.debugMode$.subscribe((mode) => {
      this.debugMode = mode;
      if (this.debugMode) {
        this.fetchDebugErrors();
      } else {
        this.registrationErrors = {};
      }
    });
  }

  public fetchDebugErrors() {
    if (this.serviceId) {
      const cachedResults = this.store.state.common.dryRunResults.get(this.serviceId);
      if (cachedResults) {
        this.processErrors(cachedResults);
      } else {
        this.serviceControllerService.recoverOrphanConfig(this.serviceId).subscribe((errors) => {
          this.store.state.common.dryRunResults.set(this.serviceId, errors);
          this.processErrors(errors);
        });
      }
    }
  }

  private processErrors(errors: RecoverOrphanConfig[]) {
    this.registrationErrors = {};
    if (errors) {
      errors.forEach((error) => {
        if (error.parentId) {
          this.registrationErrors[error.parentId] = (this.registrationErrors[error.parentId] || 0) + 1;

        }
        /*
        if (error.objectId) {
          this.registrationErrors[error.objectId] = (this.registrationErrors[error.objectId] || 0) + 1;
        }*/
      });
    }
    console.log('Processed errors:', this.registrationErrors);
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public init() {
    this.getServiceRegistrations(this.serviceId);
  }

  public onNewFilterBy(value) {
    this.newCustomFilterBy = value;
    this.newRegistration.name = this.newCustomFilterBy;
    this.newRegistration.shortName = this.newCustomFilterBy ? this.newCustomFilterBy.substring(0, 20) : "";
  }

  public addNewRegistration(newRegistration: Registration) {
    if (newRegistration) {
      this.registrationController.createRegistration(newRegistration).subscribe(
        (data) => {
          this.init();
          this.responseSuccessful("Registration created!");
          this.router.navigate(["services", this.serviceId, "registrations", data.id]);
        },
        (error) => this.showErrorMessage(error),
      );
    } else {
      this.router.navigate(["/services/" + this.serviceId + "/registrations/new"]);
    }
  }

  public toggleOperational(registration: Registration) {
    registration.isLoading = true;
    registration.active = !registration.active;
    this.registrationController.saveRegistration(this.serviceId, registration).subscribe(
      (data) => {
        registration.isLoading = false;
        if (registration.active) {
          this.responseSuccessful("Registration activated!");
        } else {
          this.responseSuccessful("Registration inactivated!");
        }
      },
      (error) => {
        this.init();
        return this.showErrorMessage(error);
      },
    );
  }

  public deleteRegistration(registration: Registration) {
    this.registrationController.deleteRegistration(registration.id).subscribe(
      (data) => {
        this.responseSuccessful("Registration deleted!");
        this.init();
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public updateRegistration(registration: Registration) {
    this.registrationController.saveRegistration(this.serviceId, registration).subscribe(
      (data) => {
        registration.isInRenameMode = false;
        registration.isLoading = false;
        this.responseSuccessful("Registration saved!");
      },
      (error) => {
        this.init();
        return this.showErrorMessage(error);
      },
    );
  }

  public sortRegistrations(registrations: Registration[]) {
    this.registrationController.updateSortOrder(registrations).subscribe(
      (data) => {
        this.serviceRegistrations = registrations;
        this.responseSuccessful("Registration reordered!");
      },
      (error) => {
        this.init();
        return this.showErrorMessage(error);
      },
    );
  }

  public addRegistrationToService(registration: Registration) {
    this.registrationController.addRegistrationToService(this.serviceId, registration.id).subscribe(
      () => {
        this.getServiceRegistrations(this.serviceId);
        this.responseSuccessful("Registration added to service!");
      },
      (error) => {
        this.init();
        return this.showErrorMessage(error);
      },
    );
  }

  public removeRegistrationFromService(registration: Registration) {
    this.registrationController.removeRegistrationFromService(this.serviceId, registration.id).subscribe(
      () => {
        this.getServiceRegistrations(this.serviceId);
        this.responseSuccessful("Registration removed from service!");
      },
      (error) => {
        this.init();
        return this.showErrorMessage(error);
      },
    );
  }

  public filterAllRegistrations() {
    if (this.loadedAllRegistrations && this.loadedServiceRegistrations) {
      this.filteredAllRegistrations = _.differenceBy(this.allRegistrations, this.serviceRegistrations, "id")
        .sort((a, b) => {
          if (a.name < b.name) { return -1; }
          if (a.name > b.name) { return 1; }
          return 0;
        });
    }
  }

  public activeRegistrations() {
    if (this.loadedAllRegistrations) {
      this.serviceRegistrationActive = this.serviceRegistrations.filter((active) => active.active);
    }
  }

  public getServiceRegistrations(serviceId: string) {
    this.registrationController.getRegistrationsList(serviceId).subscribe(
      (data) => {
        this.serviceRegistrations = _.orderBy(data, ["sortOrder"]);
        if (this.serviceRegistrations.length > 0) {
          this.defaultOtherRegListCollapsed = true;
        }
        this.loadedServiceRegistrations = true;
        this.getAllRegistrations();
      },
      (error) => this.showErrorMessageWithTitle("Unable to get list of service registrations", error),
    );
  }

  public getAllRegistrations() {
    this.registrationController.getAllRegistrations().subscribe(
      (data) => {
        this.allRegistrations = data;
        this.loadedAllRegistrations = true;
        this.activeRegistrations();
        this.filterAllRegistrations();
      },
      (error) => this.showErrorMessageWithTitle("Unable to get list of all registrations", error),
    );
  }

  public closeLayer() {
    this.showRegistrationAdd = false;
  }
}
